import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SuperAdminLayout } from "@/components/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  BarChart3,
  TrendingUp,
  Award,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-hot-toast";

const API = process.env.REACT_APP_BACKEND_URL || "";

const getAdminAuthHeader = () => {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function SuperAdminPerformanceReports() {
  const navigate = useNavigate();

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);

    const from = thirtyDaysAgo.toISOString().split("T")[0];
    const to = today.toISOString().split("T")[0];

    setDateFrom(from);
    setDateTo(to);
    fetchReport(from, to);
  }, []);

  const fetchReport = async (from, to) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/super-admin/reports/performance`,
        {
          date_from: from,
          date_to: to,
        },
        { headers: getAdminAuthHeader() }
      );
      setReport(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load performance report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Select date range");
      return;
    }
    fetchReport(dateFrom, dateTo);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  return (
    <SuperAdminLayout>
      <div>
        <div className="mb-8 flex w-full items-center px-6 bg-gradient-to-r from-orange-500 to-red-500 shadow-md text-white py-[1.65rem]">
          <Button
            onClick={() => navigate("/admin/dashboard")}
            variant="ghost"
            className="hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Performance Reports</h1>
            <p className="mt-1">
              Analyze restaurant performance and operational metrics
            </p>
          </div>
        </div>

        <div className="p-8">
          {/* Date Filter */}
          <Card className="mb-8">
            <CardContent className="p-6 flex gap-4 items-center">
              <Calendar className="h-12 w-12 text-gray-400" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span>to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
              <Button onClick={handleGenerate} disabled={loading}>
                {loading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
                Generate
              </Button>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-12 w-12 animate-spin mx-auto mb-3 text-gray-400" />
              <p>Loading performance data...</p>
            </div>
          ) : report ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card>
                  <div className="flex px-6 gap-2 py-5 items-center">
                    <Award className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-sm">Top Performer</CardTitle>
                  </div>
                  <CardContent>
                    <div className="font-bold text-lg">
                      {report.top_performer?.name || "-"}
                    </div>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(report.top_performer?.total_revenue)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <div className="flex px-6 gap-2 py-5 items-center">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <CardTitle className="text-sm">Growth Leader</CardTitle>
                  </div>
                  <CardContent>
                    <div className="font-bold text-lg">Leader Name</div>
                    <div className="text-2xl font-bold text-green-600">
                      {report.growth_leader?.name || "-"}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <div className="flex px-6 gap-2 py-5 items-center">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <CardTitle className="text-sm">Needs Attention</CardTitle>
                  </div>
                  <CardContent>
                    <div className="font-bold text-lg">Count</div>
                    <div className="text-2xl font-bold text-red-600">
                      {report.needs_attention_count}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <div className="flex px-6 gap-2 py-5 items-center">
                    <Award className="h-5 w-5 text-yellow-600" />
                    <CardTitle className="text-sm">Avg Rating</CardTitle>
                  </div>
                  <CardContent>
                    <div className="font-bold text-lg">Rating</div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {report.avg_rating || "-"}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Top 10 Restaurants by Revenue</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {report.top_10_restaurants?.map((r, i) => (
                      <div
                        key={r.restaurant_id}
                        className="flex justify-between bg-gray-50 p-3 rounded"
                      >
                        <span>
                          {i + 1}. {r.name}
                        </span>
                        <span className="font-semibold">
                          {formatCurrency(r.total_revenue)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Low Performing Outlets</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {report.low_performing_restaurants?.length ? (
                      report.low_performing_restaurants.map((r) => (
                        <div
                          key={r.restaurant_id}
                          className="p-3 border rounded"
                        >
                          <p className="font-medium">{r.name}</p>
                          <p className="text-sm text-gray-500">
                            Orders: {r.total_orders}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center">
                        No low performers 🎉
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </SuperAdminLayout>
  );
}
